// Domů — „Noční kokpit". Dva stavy:
//  · bez aktivní směny = dlaždice směny (kurzy, SPZ, statistiky, Začít směnu)
//  · s aktivní směnou  = „Na trase": další zastávka + akce (pauza, tankování)
// Logika (stores, GPS/baterie hlídání, aktivace dispečinkem) beze změny.

import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import type { RouteForToday, Vehicle } from "@routera/shared";
import { stopKey } from "@routera/shared";
import { api } from "../lib/api";
import { useHistory } from "react-router-dom";
import { IonContent, IonPage, IonRefresher, IonRefresherContent, useIonAlert } from "@ionic/react";
import { useAuthStore } from "../store/authStore";
import { LAST_PATH_KEY } from "../App";
import { useShiftStore } from "../store/shiftStore";
import { useStopProgressStore } from "../store/stopProgressStore";
import { useGpsStore } from "../store/gpsStore";
import { LamaEntry } from "../components/LamaEntry";
import { useSyncStore } from "../store/syncStore";
import { ThemeToggle } from "../components/ThemeToggle";
import { hardRefresh } from "../lib/refresh";
import { isBatteryExempt, requestBatteryExempt, openAppSettings, isSamsung } from "../lib/batteryOpt";
import { pauseStarted, pauseEnded, pauseMinutes, pauseRunningSeconds, shiftHhMm } from "../lib/pauseEta";

let _autoNavDone = false;

function greeting(): string {
  const h = new Date().getHours();
  return h < 9 ? "Dobré ráno" : h < 18 ? "Dobrý den" : "Dobrý večer";
}

function todayLabel(): string {
  return new Date().toLocaleDateString("cs-CZ", { weekday: "long", day: "numeric", month: "long" });
}

export function HomeScreen() {
  const history = useHistory();
  const driver = useAuthStore((s) => s.driver);
  const logout = useAuthStore((s) => s.logout);
  const [presentAlert] = useIonAlert();
  const { shift, loading, refresh, setShift } = useShiftStore();
  const setProgressShift = useStopProgressStore((s) => s.setShift);
  const hydrateProgress = useStopProgressStore((s) => s.hydrateFromBackend);
  const progress = useStopProgressStore((s) => s.progress);
  const gpsTracking = useGpsStore((s) => s.tracking);
  const gpsPermission = useGpsStore((s) => s.permission);
  const lastGps = useGpsStore((s) => s.lastPosition);
  const startGps = useGpsStore((s) => s.start);
  const pendingCount = useSyncStore((s) => s.pendingCount);
  const syncing = useSyncStore((s) => s.syncing);
  const online = useSyncStore((s) => s.online);

  // Optimalizace baterie — bez výjimky uspí správa baterie výrobce i appku s běžící
  // foreground service a poloha se sbírá jen když ji řidič probudí (naměřeno na produkci).
  const [batteryOk, setBatteryOk] = useState<boolean | null>(null);
  const [samsung, setSamsung] = useState(false);
  useEffect(() => {
    let dead = false;
    void (async () => {
      const [ok, sam] = await Promise.all([isBatteryExempt(), isSamsung()]);
      if (!dead) { setBatteryOk(ok); setSamsung(sam); }
    })();
    return () => { dead = true; };
  }, [shift?.id]);
  useEffect(() => {
    const recheck = () => { void isBatteryExempt().then(setBatteryOk); };
    document.addEventListener("visibilitychange", recheck);
    return () => document.removeEventListener("visibilitychange", recheck);
  }, []);

  const isPaused = shift?.is_paused ?? false;
  const [pauseLocked, setPauseLocked] = useState(false);
  const pauseMut = useMutation({
    mutationFn: (paused: boolean) => api.shift.pause(paused),
    onMutate: (paused) => {
      if (shift) {
        setShift({ ...shift, is_paused: paused });
        if (paused) pauseStarted(shift.id); else pauseEnded(shift.id);
      }
      setPauseLocked(true);
      setTimeout(() => setPauseLocked(false), 3000);
    },
    onError: () => {
      if (shift) {
        setShift({ ...shift, is_paused: !isPaused });
        if (isPaused) pauseStarted(shift.id); else pauseEnded(shift.id);
      }
      setPauseLocked(false);
    },
  });

  // Živý čítač běžící pauzy (tick 1 s jen dokud pauza běží).
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (!isPaused) return;
    const id = setInterval(() => forceTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [isPaused]);

  const { data: routes = [] } = useQuery<RouteForToday[]>({
    queryKey: ["mobile", "route", "today"],
    queryFn: () => api.route.today(),
    enabled: online,
    staleTime: 5 * 60_000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
  const { data: vehicles = [] } = useQuery<Vehicle[]>({
    queryKey: ["mobile", "vehicles"],
    queryFn: () => api.vehicles.list(),
    enabled: online,
    staleTime: 10 * 60_000,
  });
  const vehicleOf = (id?: string | null) => (id ? vehicles.find((v) => v.id === id) : undefined);

  const regularRoutes = routes.filter((r) => r.plan_name !== "TRANK");
  const trankRoutes = routes.filter((r) => r.plan_name === "TRANK");

  // Bezpečnostní gate (svoz hotovosti): dokud dispečer neaktivuje, trasa je skrytá.
  const { data: routeStatus } = useQuery({
    queryKey: ["mobile", "route", "status"],
    queryFn: () => api.route.status(),
    enabled: online,
    staleTime: 30_000,
    refetchOnMount: true,
    refetchInterval: 45_000,
  });
  const waitingActivation = !!routeStatus && routeStatus.has_assignment && !routeStatus.activated;

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    if (shift?.id && !shift.end_ts) {
      setProgressShift(shift.id);
      void hydrateProgress(shift.id);
    }
  }, [shift?.id, shift?.end_ts, setProgressShift, hydrateProgress]);

  useEffect(() => {
    if (!loading && shift && !shift.end_ts && !_autoNavDone) {
      _autoNavDone = true;
      const saved = localStorage.getItem(LAST_PATH_KEY);
      if (saved && saved.startsWith("/route")) {
        history.replace(saved);
      }
    }
  }, [loading, shift?.id, shift?.end_ts, history]);

  useEffect(() => {
    if (shift && !shift.end_ts && !gpsTracking && driver?.id) {
      void startGps(shift.id, driver.id);
    }
  }, [shift?.id, shift?.end_ts, gpsTracking, driver?.id]);

  const hasActiveShift = !!(shift && !shift.end_ts);
  const shiftStartedAt = shift?.start_ts
    ? new Date(shift.start_ts).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })
    : null;

  // ── Aktivní směna: trasa + další zastávka ─────────────────────────────────
  const activeRoute = hasActiveShift
    ? routes.find((r) => r.active_shift_id === shift!.id) ?? routes[0]
    : undefined;
  const statusOf = (id: string) => progress[id]?.status ?? "waiting";
  let nextStop: { stop: any; idx: number } | null = null;
  let doneOnRoute = 0;
  if (activeRoute) {
    activeRoute.stops.forEach((raw, idx) => {
      const s = raw as any;
      if (s.storno) return;
      if (s.collection) { if (s.done) doneOnRoute++; return; }
      const id: string = s.stop_key ?? stopKey(s);
      const st = statusOf(id);
      if (st === "done" || st === "futile") { doneOnRoute++; return; }
      if (!nextStop) nextStop = { stop: s, idx };
    });
  }
  const pauseShiftMin = hasActiveShift ? pauseMinutes(shift!.id) : 0;
  const nextEta: string | undefined = nextStop
    ? ((nextStop as { stop: any }).stop.eta
        ? shiftHhMm(String((nextStop as { stop: any }).stop.eta), pauseShiftMin)
        : undefined)
    : undefined;

  const pauseSecs = hasActiveShift && isPaused ? pauseRunningSeconds(shift!.id) : 0;
  const pauseLabel = isPaused
    ? `běží ${Math.floor(pauseSecs / 60)}:${String(pauseSecs % 60).padStart(2, "0")}`
    : "vypnutá";

  // ── CTA (bez směny): aktivní trasa > první nezačatá ───────────────────────
  // TRANK-only řidič (case Cach 25. 8.) startuje směnu ÚPLNĚ stejně jako svozový
  // — svozové trasy mají přednost, ale samotný TRANK CTA dostane taky.
  const ctaRoutes = regularRoutes.length ? regularRoutes : trankRoutes;
  const enterRoute = ctaRoutes.find((r) => r.route_status === "active" || r.active_shift_id);
  const startRoute = ctaRoutes.find(
    (r) => r.route_status !== "done" && r.route_status !== "active" && !r.active_shift_id,
  );

  const routeCard = (r: RouteForToday, i: number, isTrank = false) => {
    const status = r.route_status ?? (r.active_shift_id ? "active" : "not_started");
    const isDone = status === "done";
    const isActive = status === "active";
    const veh = vehicleOf(r.vehicle_id);
    const first = r.stops.find((s: any) => !s.storno && !s.collection) as any;
    const open = () => {
      // I TRANK jde přes start směny (fotky + km) — do trasy až s běžící směnou.
      // Výjimka: TRANK sloučený do svozové trasy sem vůbec nedojde (jedna karta).
      if (isActive) history.push(`/route?rid=${r.route_id}`);
      else if (!isDone) history.push(`/shift/start?rid=${r.route_id}`);
    };
    return (
      <button
        key={r.route_id}
        className={`card goldedge${isTrank ? " trankdash" : ""}`}
        onClick={open}
        disabled={isDone}
        style={{
          width: "100%", textAlign: "left", display: "block",
          marginTop: i === 0 ? 16 : 12, opacity: isDone ? 0.55 : 1,
        }}
      >
        <div className="cap" style={{ display: "flex" }}>
          {isTrank
            ? "Kurz TRANK"
            : regularRoutes.length + trankRoutes.length > 1 ? `Kurz ${i + 1}` : "Dnešní trasa"}
          <span className="spacer" />
          {isDone && <span style={{ color: "var(--ok)", letterSpacing: 0 }}>✓ hotovo</span>}
          {isActive && <span style={{ color: "var(--ok)", letterSpacing: 0 }}>právě jedeš</span>}
        </div>
        <div style={{ textAlign: "center" }}>
          <div className="bigstat" style={{ justifyContent: "center" }}>
            <b style={{ fontSize: 26 }}>{r.name ?? (isTrank ? "TRANK" : "Trasa")}</b>
          </div>
          {veh && (
            <div style={{ marginTop: 8 }}>
              <div className="mono" style={{ fontSize: 19, fontWeight: 800, letterSpacing: ".1em" }}>{veh.spz}</div>
              <div style={{ marginTop: 3, color: "var(--dim)", fontSize: 12.5 }}>{veh.name}</div>
            </div>
          )}
        </div>
        <div className="statstrip">
          <div><b className="mono">{r.stops.length}</b><span>zastávek</span></div>
          <div><b className="mono">{typeof r.km === "number" ? Math.round(r.km) : "—"}</b><span>km</span></div>
          <div><b className="mono">{typeof r.duration_h === "number" ? `~${Math.round(r.duration_h)}` : "—"}</b><span>hodin</span></div>
        </div>
        {first && (
          <div className="row" style={{ marginTop: 12, fontSize: 13, color: "var(--soft)" }}>
            první zastávka
            <b style={{ color: "var(--ink)", marginLeft: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {first.branch_id ? `${first.branch_id} ` : ""}{String(first.name ?? "").toUpperCase()}
            </b>
            <div className="spacer" />
            {first.window_from && first.window_to && (
              <b className="mono" style={{ color: "var(--ink)", flex: "none" }}>{first.window_from}–{first.window_to}</b>
            )}
          </div>
        )}
      </button>
    );
  };

  return (
    <IonPage>
      <IonContent>
        <IonRefresher
          slot="fixed"
          onIonRefresh={(e) => void hardRefresh().finally(() => e.detail.complete())}
        >
          <IonRefresherContent pullingText="Táhni dolů pro obnovu" refreshingSpinner="crescent" />
        </IonRefresher>

        <div className="ck scenebg" style={{ minHeight: "100%" }}>
          <div className="top">
            <span className="wm">ROUTERA <em>DRIVER</em></span>
            {hasActiveShift ? (
              <span className="live"><span className="dot" />živě</span>
            ) : (
              <span className="live" style={{ color: "var(--dim)" }}>
                <span className="dot" style={{ background: "var(--dim)", animation: "none" }} />
                směna nezačala
              </span>
            )}
            <ThemeToggle />
          </div>

          <div className="stopbody"><div className="pad-x">
            {!hasActiveShift ? (
              <>
                <h1>{greeting()}, {driver?.name?.split(" ")[0] ?? "řidiči"}</h1>
                <div className="h-sub">{todayLabel()}</div>

                {loading ? (
                  <div style={{ textAlign: "center", padding: "32px 0", color: "var(--dim)", fontSize: 14 }}>
                    Načítám…
                  </div>
                ) : (
                  <>
                    {regularRoutes.map((r, i) => routeCard(r, i))}
                    {trankRoutes.map((r, i) => routeCard(r, regularRoutes.length + i, true))}

                    {regularRoutes.length === 0 && trankRoutes.length === 0 && (
                      waitingActivation ? (
                        <div className="card" style={{ marginTop: 16, borderColor: "rgba(245,174,69,.45)", background: "var(--warn-dim)" }}>
                          <b style={{ color: "var(--warn)", fontSize: 14.5 }}>Čeká na aktivaci dispečinkem</b>
                          <div style={{ fontSize: 12.5, color: "var(--soft)", marginTop: 4 }}>
                            Trasa se zobrazí, jakmile ji dispečer uvolní.
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding: "26px 0", color: "var(--dim)", fontSize: 14, textAlign: "center" }}>
                          Dnes nemáš přiřazenou trasu.
                        </div>
                      )
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <h1>Na trase</h1>
                <div className="h-sub">
                  {activeRoute?.name ?? "Směna"}
                  {shiftStartedAt ? ` · směna od ${shiftStartedAt}` : ""}
                  {activeRoute ? ` · ${doneOnRoute} z ${activeRoute.stops.filter((s: any) => !s.storno).length} hotovo` : ""}
                </div>

                {nextStop && (
                  <button
                    className="card"
                    style={{ width: "100%", textAlign: "left", marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}
                    onClick={() => {
                      const s = (nextStop as { stop: any; idx: number });
                      const rid = activeRoute ? `?rid=${activeRoute.route_id}` : "";
                      history.push(`/route/stop/${s.idx}${rid}`);
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="cap" style={{ display: "block", marginBottom: 2 }}>Další zastávka</span>
                      <b style={{ fontSize: 15.5 }}>
                        {(nextStop as { stop: any }).stop.branch_id ? `${(nextStop as { stop: any }).stop.branch_id} ` : ""}
                        {String((nextStop as { stop: any }).stop.name ?? "Pobočka").toUpperCase()}
                      </b>
                      <span style={{ display: "block", fontSize: 12.5, color: "var(--soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {(nextStop as { stop: any }).stop.address ?? ""}
                      </span>
                    </span>
                    {nextEta && (
                      <span style={{ textAlign: "right", flex: "none" }}>
                        <b className="mono" style={{ fontSize: 17, display: "block" }}>~{nextEta}</b>
                        <span style={{ display: "block", fontSize: 10.5, color: "var(--dim)" }}>příjezd</span>
                      </span>
                    )}
                  </button>
                )}

                <div className="cap" style={{ marginTop: 18 }}>Akce</div>
                <button
                  className={`actrow${isPaused ? " onrun" : ""}`}
                  disabled={pauseMut.isPending || pauseLocked}
                  onClick={() => pauseMut.mutate(!isPaused)}
                >
                  <span className="aic"><svg viewBox="0 0 24 24"><path d="M9 5v14M15 5v14" /></svg></span>
                  <span style={{ minWidth: 0 }}><b>Bezpečnostní pauza</b></span>
                  <span className="astate">{pauseLabel}</span>
                </button>
                <button className="actrow" onClick={() => history.push("/shift/fueling")}>
                  <span className="aic"><svg viewBox="0 0 24 24"><path d="M4 21V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v15" /><path d="M2.5 21h13" /><path d="M6.5 8h5" /><path d="M14 11h2.5a2 2 0 0 1 2 2v4.5a1.5 1.5 0 0 0 3 0V10l-3-3" /></svg></span>
                  <span style={{ minWidth: 0 }}><b>Tankování</b></span>
                  <span className="astate">›</span>
                </button>

                {pauseShiftMin > 0 && (
                  <div style={{ marginTop: 8, fontSize: 12, color: "var(--dim)", textAlign: "center" }}>
                    Odhady příjezdů posunuté o {pauseShiftMin} min pauzy.
                  </div>
                )}

                {batteryOk === false && (
                  <div className="card" role="alert" style={{ marginTop: 12, borderColor: "rgba(245,174,69,.5)", background: "var(--warn-dim)" }}>
                    <b style={{ fontSize: 14.5, color: "var(--warn)" }}>Telefon může polohu uspat</b>
                    <div style={{ marginTop: 4, fontSize: 13, lineHeight: 1.45, color: "var(--soft)" }}>
                      {samsung
                        ? "Samsung uspává aplikace na pozadí a poloha pak přestane chodit. Povol Routeře běh bez omezení a zkontroluj, že není mezi uspávanými aplikacemi."
                        : "Povol Routeře běh bez omezení baterie, jinak se poloha na pozadí přestane odesílat."}
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                      <button className="cta" style={{ flex: 1, minWidth: 130, padding: "11px 14px", fontSize: 14 }} onClick={() => void requestBatteryExempt()}>
                        Povolit
                      </button>
                      {samsung && (
                        <button className="cta quiet" style={{ flex: 1, minWidth: 130, padding: "11px 14px", fontSize: 14, marginTop: 0 }} onClick={() => void openAppSettings()}>
                          Nastavení baterie
                        </button>
                      )}
                    </div>
                    {samsung && (
                      <div style={{ marginTop: 8, fontSize: 12, color: "var(--dim)", lineHeight: 1.4 }}>
                        V nastavení: Baterie → Omezení používání na pozadí → Routera Driver
                        musí být „Neomezeno" a nesmí být v seznamu uspávaných aplikací.
                      </div>
                    )}
                  </div>
                )}

                <div className="row" style={{ marginTop: 14, fontSize: 12.5, color: "var(--dim)", justifyContent: "center" }}>
                  <span className="dot" style={{
                    width: 7, height: 7, animation: "none",
                    background: gpsTracking ? "var(--ok)" : gpsPermission === "denied" ? "var(--bad)" : "var(--warn)",
                  }} />
                  {gpsTracking ? "GPS aktivní" : gpsPermission === "denied" ? "GPS zakázáno" : "GPS nespuštěno"}
                  {lastGps && (
                    <span className="mono">
                      {new Date(lastGps.ts).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                  {(pendingCount > 0 || syncing) && <span>{syncing ? "sync…" : `${pendingCount} čeká`}</span>}
                </div>

                {/* Odhlášení i BĚHEM směny — dispečer se často přihlašuje na
                    řidiče kvůli kontrole a nesmí zůstat uvězněný do konce
                    směny. Směna běží na serveru dál, řidič po přihlášení
                    pokračuje kde skončil (progress se hydratuje z events). */}
                <div style={{ display: "flex", justifyContent: "center", marginTop: 14 }}>
                  <button
                    className="ghostbtn"
                    onClick={() => {
                      presentAlert({
                        header: "Odhlásit se?",
                        message: "Směna zůstane běžet. Po dalším přihlášení řidič pokračuje tam, kde skončil.",
                        buttons: [
                          { text: "Zrušit", role: "cancel" },
                          { text: "Odhlásit se", role: "destructive", handler: () => void logout() },
                        ],
                      });
                    }}
                  >
                    <svg viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>
                    Odhlásit se
                  </button>
                </div>
              </>
            )}

            {/* Lama Klub — jen mimo směnu (během směny k němu řidič nemá lézt) */}
            {!hasActiveShift && !loading && (
              <div className="pad-x"><LamaEntry /></div>
            )}

            {!hasActiveShift && !loading && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
                <button className="ghostbtn" onClick={logout}>
                  <svg viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>
                  Odhlásit se
                </button>
              </div>
            )}
          </div></div>

          <div className="bottom">
            {hasActiveShift ? (
              <button className="cta" onClick={() => history.push(activeRoute ? `/route?rid=${activeRoute.route_id}` : "/route")}>
                Zpět na trasu
              </button>
            ) : enterRoute ? (
              <button className="cta" onClick={() => history.push(`/route?rid=${enterRoute.route_id}`)}>
                Pokračovat · {enterRoute.name ?? "trasa"}
              </button>
            ) : startRoute ? (
              <button className="cta" onClick={() => history.push(`/shift/start?rid=${startRoute.route_id}`)}>
                Začít směnu
              </button>
            ) : null}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
